import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, ShieldCheckIcon, DatabaseIcon, RefreshCwIcon, ActivityIcon, PuzzleIcon, HardDriveIcon, EyeIcon, GaugeIcon, ZapIcon, LockIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function HostingOnderhoudPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Hosting & onderhoud | Zorgeloos online | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Website hosting en onderhoud zonder zorgen. Snelle servers, dagelijkse backups, beveiligingsupdates en 24/7 monitoring. Noord Digital ontzorgt je volledig.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Hosting & onderhoud | Zorgeloos online | Noord',
      'og:description': 'Website hosting en onderhoud zonder zorgen. Snelle servers, dagelijkse backups en 24/7 monitoring.',
      'og:type': 'website',
      'og:url': 'https://noorddigital.nl/hosting-onderhoud',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const scrollToSection = (e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Heb ik website onderhoud nodig?',
    a: 'Ja, elke website heeft onderhoud nodig. WordPress draait op software die regelmatig wordt bijgewerkt. Zonder updates loop je risico op beveiligingslekken, trage laadtijden en compatibiliteitsproblemen. Met ons onderhoudspakket hoef je je hier geen zorgen over te maken.'
  }, {
    q: 'Hoe vaak wordt mijn website geüpdatet?',
    a: 'Wij controleren je website wekelijks op beschikbare updates. Kritieke beveiligingsupdates worden direct doorgevoerd. Reguliere plugin- en thema-updates voeren wij maandelijks uit, altijd na een grondige test.'
  }, {
    q: 'Wat gebeurt er bij problemen?',
    a: 'Bij problemen grijpen wij direct in. Dankzij onze monitoring detecteren we issues vaak voordat jij ze merkt. Mocht er toch iets misgaan, dan herstellen we je website snel vanuit een recente backup. Je kunt ons altijd bereiken voor ondersteuning.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-1104">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-1105">
        <div className="absolute inset-0 pointer-events-none" data-id="element-1106">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-1107" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-1108" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-1109" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1110">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-1111">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-1112">
              Home
            </a>
            <span className="text-navy-500" data-id="element-1113">/</span>
            <span className="text-accent-light font-medium" data-id="element-1114">
              Hosting & onderhoud
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-1115">
            Hosting en onderhoud zonder zorgen
          </h1>
          <p className="text-lg text-slate-600 text-center max-w-2xl mx-auto mb-12 lg:mb-16" data-id="element-1116">
            Snelle servers, dagelijkse back-ups en geen omkijken naar updates of
            technisch gedoe. Wij regelen het.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-1117">
            <a href="#pakketten" onClick={e => scrollToSection(e, 'pakketten')} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-1118">
              Bekijk onderhoudspakketten
              <ArrowRightIcon className="w-5 h-5" data-id="element-1119" />
            </a>
          </div>
        </div>
      </section>

      {/* Waarom onderhoud belangrijk is */}
      <section className="py-24 bg-surface-muted" data-id="element-1120">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1121">
          <div className="text-center mb-16" data-id="element-1122">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1123">
              Waarom website onderhoud belangrijk is
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1124">
              Een website zonder onderhoud is als een auto zonder APK. Het gaat
              een tijdje goed, maar uiteindelijk loop je vast.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1125">
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-1126">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1127">
                <ShieldCheckIcon className="w-6 h-6 text-accent" data-id="element-1128" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1129">
                Beveiliging
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1130">
                Zonder updates wordt je website kwetsbaar voor hackers en
                malware. Wij houden je website veilig.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-1131">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1132">
                <DatabaseIcon className="w-6 h-6 text-accent" data-id="element-1133" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1134">Backups</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1135">
                Dagelijkse backups zorgen ervoor dat je nooit data verliest. Bij
                problemen zetten we alles snel terug.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-1136">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1137">
                <RefreshCwIcon className="w-6 h-6 text-accent" data-id="element-1138" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1139">Updates</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1140">
                WordPress, thema's en plugins hebben regelmatig updates nodig om
                goed en veilig te blijven werken.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-1141">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1142">
                <ActivityIcon className="w-6 h-6 text-accent" data-id="element-1143" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1144">
                Monitoring
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1145">
                Wij monitoren je website 24/7 op uptime en prestaties. Problemen
                lossen we op voordat jij ze merkt.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Wat inbegrepen is */}
      <section id="pakketten" className="py-24 bg-white" data-id="element-1146">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1147">
          <div className="text-center mb-16" data-id="element-1148">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1149">
              Wat zit er in ons onderhoudspakket?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1150">
              Alles wat je nodig hebt voor een zorgeloze website, standaard
              inbegrepen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1151">
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-1152">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-1153">
                <PuzzleIcon className="w-6 h-6 text-navy-700" data-id="element-1154" />
              </div>
              <div data-id="element-1155">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1156">
                  Plugin updates
                </h3>
                <p className="text-slate-600" data-id="element-1157">
                  Alle plugins en thema's worden maandelijks bijgewerkt naar de
                  nieuwste versie.
                </p>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-1158">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-1159">
                <HardDriveIcon className="w-6 h-6 text-navy-700" data-id="element-1160" />
              </div>
              <div data-id="element-1161">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1162">
                  Dagelijkse backups
                </h3>
                <p className="text-slate-600" data-id="element-1163">
                  Elke dag wordt er automatisch een volledige backup gemaakt van
                  je website en database.
                </p>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-1164">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-1165">
                <EyeIcon className="w-6 h-6 text-navy-700" data-id="element-1166" />
              </div>
              <div data-id="element-1167">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1168">
                  Security monitoring
                </h3>
                <p className="text-slate-600" data-id="element-1169">
                  Continue bewaking op malware, verdachte activiteit en
                  ongeautoriseerde toegangspogingen.
                </p>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-1170">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-1171">
                <GaugeIcon className="w-6 h-6 text-navy-700" data-id="element-1172" />
              </div>
              <div data-id="element-1173">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1174">
                  Performance optimalisatie
                </h3>
                <p className="text-slate-600" data-id="element-1175">
                  Regelmatige controle en optimalisatie van laadsnelheid,
                  caching en database.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hosting voordelen */}
      <section className="py-24 bg-navy-50" data-id="element-1176">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1177">
          <div className="text-center mb-16" data-id="element-1178">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1179">
              Betrouwbare hosting voor jouw website
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1180">
              Onze hosting is speciaal geoptimaliseerd voor WordPress websites.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1181">
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-1182">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1183">
                <ZapIcon className="w-6 h-6 text-accent" data-id="element-1184" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1185">
                Snelle servers
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1186">
                Onze servers zijn geoptimaliseerd voor WordPress. Jouw website
                laadt razendsnel, altijd.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-1187">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1188">
                <DatabaseIcon className="w-6 h-6 text-accent" data-id="element-1189" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1190">
                Dagelijkse backups
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1191">
                Automatische dagelijkse backups met één-klik herstel. Je data is
                altijd veilig.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-1192">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1193">
                <LockIcon className="w-6 h-6 text-accent" data-id="element-1194" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1195">
                SSL beveiliging
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1196">
                Gratis SSL-certificaat inbegrepen. Je website is beveiligd en
                Google waardeert dat.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-surface-muted border-y border-navy-100" data-id="element-1197">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1198">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-1199">
            Ontdek meer:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-1200">
            <a href="#/website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1201">
              Nieuwe website laten bouwen
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-1202">•</span>
            <a href="#/wordpress-websites" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1203">
              WordPress specialist
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-1204">•</span>
            <a href="#/website-laten-maken-checklist" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1205">
              Checklist voor je website
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-1206">•</span>
            <a href="#/" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1207">
              Terug naar homepage
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white" data-id="element-1208">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1209">
          <div className="text-center mb-16" data-id="element-1210">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1211">
              Veelgestelde vragen over website onderhoud
            </h2>
          </div>

          <div className="space-y-4" data-id="element-1212">
            {faqs.map((faq, index) => <div key={index} className="bg-surface-muted rounded-xl shadow-sm border border-navy-50 overflow-hidden" data-id="element-1213">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-1214">
                  <span className="font-bold text-navy-900" data-id="element-1215">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1216" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-1217" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-1218">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-1219" />
    </main>;
}